'use client';

import { Fragment, ReactNode, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import { BackToTopButton } from '@/components/BackToTopButton';
import { Typography } from '@/components/Typography';
import { JumpLinkType, useJumpLinks } from '@/providers';

export interface JumpLinksNavProps {
  navTitle?: ReactNode;
  tracking: {
    pageName: string;
  };
  bulletSeparator?: boolean;
  showBackToTop?: boolean;
  classes?: {
    wrapper?: string;
    title?: string;
    buttonWrapper?: string;
    button?: string;
    bullet?: string;
  };
}

export function JumpLinksNav({
  navTitle = 'On this page',
  tracking,
  bulletSeparator = true,
  showBackToTop = true,
  classes,
}: JumpLinksNavProps) {
  const { jumpLinks } = useJumpLinks();
  const backToTopPortalRef = useRef<HTMLDivElement | null>(null);

  const defaultClasses: JumpLinksNavProps['classes'] = {
    wrapper: 'flex flex-col',
    title: 'text-tag-350 font-sans uppercase text-secondary',
    buttonWrapper: 'flex items-center flex-wrap gap-y-1 gap-x-3 md:gap-x-5',
    button: 'text-link',
    bullet: 'text-secondary',
  };

  const componentClasses = {
    ...defaultClasses,
    ...classes,
  };

  useEffect(() => {
    backToTopPortalRef.current = document.querySelector('#back-to-top-portal');
  }, [showBackToTop]);

  // TODO hoist to provider and refactor ScrollToContentButton
  function handleJumpLinkClick(jumpLink: JumpLinkType) {
    if (jumpLink.destinationRef?.current) {
      jumpLink.destinationRef.current.scrollIntoView({ behavior: 'smooth' });

      // add a hash to the URL to make it bookmarkable
      window.history.pushState(null, '', `#${jumpLink.id}`);
    }

    trackEvent(EVENT_NAMES.jumplink_click, {
      link_id: jumpLink.id,
      link_label: jumpLink.navLabel.full,
      page_name: tracking.pageName,
    });
  }

  return (
    <>
      <nav
        aria-labelledby="page-jump-links-label"
        className={componentClasses.wrapper}
      >
        {navTitle && (
          <h4 id="page-jump-links-label" className={componentClasses.title}>
            {navTitle}
          </h4>
        )}
        {!!jumpLinks?.length && (
          <div className={componentClasses.buttonWrapper}>
            {jumpLinks.map(
              (jumpLink, index) =>
                jumpLink.destinationRef?.current && (
                  <Fragment key={jumpLink.id}>
                    <button
                      onClick={() => handleJumpLinkClick(jumpLink)}
                      className={componentClasses.button}
                    >
                      <Typography name="heading-400" tag="span">
                        {/* Desktop label */}
                        <span
                          className={classNames(
                            jumpLink.navLabel.short ? 'hidden md:inline' : '',
                          )}
                        >
                          {jumpLink.navLabel.full}
                        </span>

                        {/* Mobile label */}
                        {jumpLink.navLabel.short && (
                          <span className="md:hidden">
                            {jumpLink.navLabel.short}
                          </span>
                        )}
                      </Typography>
                    </button>
                    {bulletSeparator && index < jumpLinks.length - 1 && (
                      <span className={componentClasses.bullet}>&bull;</span>
                    )}
                  </Fragment>
                ),
            )}
          </div>
        )}
      </nav>
      {showBackToTop &&
        backToTopPortalRef.current &&
        createPortal(<BackToTopButton />, backToTopPortalRef.current)}
    </>
  );
}
